import { FacilityFlowCoreModulesOffersEntitiesPositionRecurrenceKind as RecurrenceKind } from '../api/generated/models/facilityFlowCoreModulesOffersEntitiesPositionRecurrenceKind';
import type { FacilityFlowCoreModulesOffersEntitiesPositionBillingRule } from '../api/generated/models/facilityFlowCoreModulesOffersEntitiesPositionBillingRule';
import type { FacilityFlowCoreModulesPlanningDtosScheduleRulesTaskScheduleRuleDto } from '../api/generated/models/facilityFlowCoreModulesPlanningDtosScheduleRulesTaskScheduleRuleDto';
import { ScheduleRuleType } from './scheduleRuleConstants';

/**
 * Billing kind and rule of an offer position („Abrechnung", FAFD-27). The backend decides from
 * them how often the invoice bills a position (the DTO also returns `billingMonths`); the
 * client edits and displays them, it never classifies by interval text.
 */
export type BillingKind = (typeof RecurrenceKind)[keyof typeof RecurrenceKind];
export type BillingRule = FacilityFlowCoreModulesOffersEntitiesPositionBillingRule;
type TaskScheduleRule = FacilityFlowCoreModulesPlanningDtosScheduleRulesTaskScheduleRuleDto;

export const BillingKinds = RecurrenceKind;

/** Kinds the user can choose, in display order; `Unknown` only exists for legacy positions. */
export const SELECTABLE_BILLING_KINDS: readonly BillingKind[] = [
  RecurrenceKind.Monthly,
  RecurrenceKind.Quarterly,
  RecurrenceKind.Yearly,
  RecurrenceKind.OneOff,
  RecurrenceKind.ByEffort,
];

/** A position without kind blocks billing of its object until someone sets one. */
export function needsReview(kind: BillingKind | null | undefined): boolean {
  return !kind || kind === RecurrenceKind.Unknown;
}

/** i18n key (namespace `offers`) of the kind's label. */
export function billingKindLabelKey(kind: BillingKind | null | undefined): string {
  return `offers:billing.kinds.${kind ?? RecurrenceKind.Unknown}`;
}

/** Sensible starting rule when the user switches to a kind. */
export function defaultBillingRule(kind: BillingKind, today: Date = new Date()): BillingRule {
  switch (kind) {
    case RecurrenceKind.Quarterly:
    case RecurrenceKind.Yearly:
      // why: a new customer is billed from the month the work starts, not back to January.
      return { month: today.getMonth() + 1, inArrears: false };
    default:
      return {};
  }
}

/** Client-side mirror of the backend validator, so the form can block saving early. */
export function isBillingRuleComplete(kind: BillingKind | null | undefined, rule: BillingRule): boolean {
  switch (kind) {
    case null:
    case undefined:
    case RecurrenceKind.Unknown:
      return false;
    case RecurrenceKind.Monthly:
      // A season needs both ends, or none.
      return (rule.fromMonth == null) === (rule.toMonth == null);
    default:
      return true;
  }
}

/**
 * Proposes how to bill a task from its schedule: a season becomes "monthly in these months",
 * everything else monthly. Per-visit prices are chosen as "by effort" by the user.
 */
export function billingFromTaskSchedule(rules: TaskScheduleRule[] | null | undefined): { kind: BillingKind; rule: BillingRule } {
  const seasonal = (rules ?? []).find(
    (r) => r.isActive !== false && r.type === ScheduleRuleType.Seasonal && r.windowFromMonth && r.windowToMonth,
  );
  return seasonal
    ? { kind: RecurrenceKind.Monthly, rule: { fromMonth: seasonal.windowFromMonth, toMonth: seasonal.windowToMonth } }
    : { kind: RecurrenceKind.Monthly, rule: {} };
}

/** The four months of the quarterly rhythm that contains `month`, in calendar order — like the backend. */
export function quarterMonths(month: number): number[] {
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].filter((m) => (m - month + 12) % 3 === 0);
}

const trimDot = (s: string) => s.replace(/\.$/, '');

/**
 * Short text of the rule details, e.g. "Nov–Mär", "Feb, Mai, Aug, Nov", "Juni · nachträglich" —
 * the kind label is shown separately. Empty when there is nothing to add.
 */
export function formatBillingRule(
  kind: BillingKind | null | undefined,
  rule: BillingRule | null | undefined,
  locale: string,
  inArrearsWord = '',
): string {
  if (!rule) return '';
  const monthShort = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  const monthLong = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  const monthName = (m: number, f: Intl.DateTimeFormat) => trimDot(f.format(new Date(Date.UTC(2026, m - 1, 1))));
  const arrears = rule.inArrears ? inArrearsWord : '';

  switch (kind) {
    case RecurrenceKind.Monthly:
      return rule.fromMonth && rule.toMonth
        ? `${monthName(rule.fromMonth, monthShort)}–${monthName(rule.toMonth, monthShort)}` : '';
    case RecurrenceKind.Quarterly:
      // why: the month only sets the rhythm — naming all four months hides none of them.
      return [quarterMonths(rule.month ?? 1).map((m) => monthName(m, monthShort)).join(', '), arrears]
        .filter(Boolean).join(' · ');
    case RecurrenceKind.Yearly:
      return [monthName(rule.month ?? 1, monthLong), arrears].filter(Boolean).join(' · ');
    default:
      return '';
  }
}

